<template>
    <div id="appCapsule">
        <div class="section mt-2 mb-3">

            <!-- Header -->
            <div class="support-header d-flex align-items-center justify-content-between mb-3">
                <div>
                    <h4 class="support-title mb-0">Support Messages</h4>
                    <p class="support-subtitle mb-0">{{ supportMsgs.length }} conversation{{ supportMsgs.length !== 1 ? 's' : '' }}</p>
                </div>
                <div class="unread-total-badge" v-if="totalUnread > 0">
                    {{ totalUnread }} unread
                </div>
            </div>

            <!-- Empty state -->
            <div v-if="supportMsgs.length === 0" class="empty-state text-center py-5">
                <ion-icon name="chatbubbles-outline" class="empty-icon"></ion-icon>
                <p class="mt-2 text-muted">No support messages yet</p>
            </div>

            <!-- Conversation list -->
            <div class="support-list" v-else>
                <router-link
                    v-for="(msg, idx) in supportMsgs"
                    :key="idx"
                    :to="{name:'UserSupportMessages', params: {userId: msg.id}}"
                    class="support-item"
                >
                    <!-- Avatar -->
                    <div class="support-avatar" :style="{ background: avatarColor(idx) }">
                        {{ initials(idx) }}
                    </div>

                    <!-- Info -->
                    <div class="support-info">
                        <div class="support-name">
                            <template v-if="users[idx] !== undefined">
                                {{ users[idx].data.name }}
                            </template>
                            <template v-else>
                                {{ msg.id.substr(0, 10) }}…
                            </template>
                        </div>
                        <div class="support-email" v-if="users[idx] !== undefined">
                            {{ users[idx].data.email }}
                        </div>
                    </div>

                    <!-- Unread badge + arrow -->
                    <div class="support-meta">
                        <span class="unread-badge" v-if="msg.data.unread > 0">
                            {{ msg.data.unread }}
                        </span>
                        <span class="unread-zero" v-else>—</span>
                        <ion-icon name="chevron-forward-outline" class="support-chevron"></ion-icon>
                    </div>
                </router-link>
            </div>

        </div>
    </div>
</template>

<script>
import {mapGetters} from 'vuex'

const AVATAR_COLORS = [
    '#10B981', '#059669', '#047857',
    '#0891B2', '#7C3AED', '#DB2777',
    '#D97706', '#DC2626'
];

export default {
    name: "supportMessages",
    data(){
        return {
            users: []
        }
    },
    computed: {
        ...mapGetters('support', {
            supportMsgs: 'getAllMessages'
        }),
        totalUnread(){
            return this.supportMsgs.reduce((sum, m) => sum + (m.data.unread || 0), 0);
        }
    },
    watch: {
        supportMsgs(){
            this.fetchUserMeta();
        }
    },
    methods: {
        async fetchUserMeta(){
            const userPromises = this.supportMsgs.map(msg => this.$store.dispatch('user/getUser', msg.id));
            this.users = (await Promise.all(userPromises)).map(r => r.data);
        },
        initials(idx){
            const user = this.users[idx];
            if (!user) return '?';
            const name = user.data.name || '';
            return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
        },
        avatarColor(idx){
            return AVATAR_COLORS[idx % AVATAR_COLORS.length];
        }
    },
    mounted() {
        this.$store.dispatch('support/fetchAllSupportMessages');
    }
}
</script>

<style scoped>
/* Header */
.support-title {
    font-size: 18px;
    font-weight: 700;
    color: var(--color-text, #0F172A);
    letter-spacing: -0.02em;
}
.support-subtitle {
    font-size: 12px;
    color: var(--color-muted, #64748B);
}
.unread-total-badge {
    background: var(--theme-primary-500, #10B981);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;
}

/* Empty state */
.empty-icon {
    font-size: 52px;
    color: var(--color-muted, #64748B);
}

/* List */
.support-list {
    display: flex;
    flex-direction: column;
    gap: 0;
    background: #fff;
    border: 1px solid var(--color-border, #E2E8F0);
    border-radius: 14px;
    overflow: hidden;
}

.support-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    text-decoration: none;
    border-bottom: 1px solid var(--color-border, #E2E8F0);
    transition: background 0.15s;
}
.support-item:last-child {
    border-bottom: none;
}
.support-item:active,
.support-item:hover {
    background: var(--theme-primary-50, #ECFDF5);
}

/* Avatar */
.support-avatar {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.02em;
}

/* Info */
.support-info {
    flex: 1 1 auto;
    min-width: 0;
}
.support-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text, #0F172A);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.support-email {
    font-size: 11px;
    color: var(--color-muted, #64748B);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 1px;
}

/* Meta */
.support-meta {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 6px;
}
.unread-badge {
    background: var(--theme-primary-500, #10B981);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.unread-zero {
    font-size: 13px;
    color: var(--color-muted, #64748B);
}
.support-chevron {
    font-size: 16px;
    color: var(--color-muted, #64748B);
}
</style>
